/**
 * Delivery Integrity — operator dashboard for the three-tier KIE.ai → Kaharian → Konnect
 * generation chain. Shows the two required tiles (Delivery Integrity + Financial
 * Reconciliation), a job list with attribution filters, drill-down job log, and a
 * dispute payload review queue.
 *
 * Route: /delivery-integrity  (super_admin, country_operator, platform_owner)
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Activity, AlertTriangle, DollarSign, RefreshCw, Search,
  ChevronRight, CheckCircle2, XCircle, Clock, FileText, PlayCircle,
  Cpu, Cloud, Layers,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Attribution labels — honest names per plan.
const ATTR_LABEL = {
  internal_kaharian_persistence: 'Kaharian · Persistence',
  internal_kaharian_verification: 'Kaharian · Verification',
  internal_konnect_delivery: 'Konnect · Delivery',
  internal_konnect_playback: 'Konnect · Playback',
  network_transient: 'Network · Transient',
  upstream_kie_ai: 'Upstream · KIE.ai',
};
const INTERNAL_KEYS = [
  'internal_kaharian_persistence',
  'internal_kaharian_verification',
  'internal_konnect_delivery',
  'internal_konnect_playback',
];

function fmtMoney(n) {
  if (n === undefined || n === null) return '$0.00';
  return `$${Number(n).toFixed(4).replace(/0+$/, '').replace(/\.$/, '.00')}`;
}
function fmtNum(n) { return (n ?? 0).toLocaleString(); }
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString();
}
function shortId(s) { return (s || '').slice(0, 8); }

// ── Tile 1: Delivery Integrity ────────────────────────────────────────────
const DeliveryIntegrityTile = ({ data }) => {
  if (!data) return null;
  const amber = data.amber;
  return (
    <Card
      data-testid="delivery-integrity-tile"
      className={`border-2 ${amber ? 'border-amber-300 bg-amber-50/40' : 'border-emerald-200 bg-white'}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-gray-900">
            <ShieldCheck className={`w-5 h-5 ${amber ? 'text-amber-600' : 'text-emerald-600'}`} />
            Delivery Integrity
          </CardTitle>
          <Badge variant="outline" className="text-xs">last {data.window_hours}h</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{fmtNum(data.kie_completions)}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">KIE.ai<br/>completions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{fmtNum(data.kaharian_pass)}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">Kaharian<br/>verified</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{fmtNum(data.konnect_commits)}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">Konnect<br/>commits</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${data.internal_gap_count ? 'text-amber-700' : 'text-emerald-700'}`}>
              {fmtNum(data.internal_gap_count)}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">Internal<br/>gaps</div>
          </div>
        </div>
        {Object.keys(data.attribution_split || {}).length > 0 && (
          <div className="border-t pt-3">
            <div className="text-xs font-medium text-gray-700 mb-2">Attribution split</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(data.attribution_split).map(([k, v]) => (
                <Badge
                  key={k}
                  data-testid={`attr-badge-${k}`}
                  className={
                    INTERNAL_KEYS.includes(k)
                      ? 'bg-amber-100 text-amber-900 hover:bg-amber-100'
                      : k === 'upstream_kie_ai'
                        ? 'bg-red-100 text-red-900 hover:bg-red-100'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
                  }
                >
                  {ATTR_LABEL[k] || k} · {v}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {data.internal_gap_count === 0 && data.upstream_gap_count === 0 && (
          <div className="flex items-center gap-2 text-sm text-emerald-700 border-t pt-2">
            <CheckCircle2 className="w-4 h-4" />
            All three hops delivering cleanly.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Tile 2: Financial Reconciliation ──────────────────────────────────────
const FinancialReconciliationTile = ({ data }) => {
  if (!data) return null;
  return (
    <Card data-testid="financial-reconciliation-tile" className="border-2 border-gray-200 bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-gray-900">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Financial Reconciliation
          </CardTitle>
          <Badge variant="outline" className="text-xs">last {data.window_days}d</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">KIE.ai spend</div>
            <div className="text-xl font-bold text-gray-900" data-testid="fin-spend">{fmtMoney(data.total_spend)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Konnect committed</div>
            <div className="text-xl font-bold text-emerald-700" data-testid="fin-committed">{fmtMoney(data.total_committed)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Internal loss booked</div>
            <div className={`text-xl font-bold ${data.total_internal_loss > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
              {fmtMoney(data.total_internal_loss)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Recovered</div>
            <div className="text-xl font-bold text-emerald-700">{fmtNum(data.recovered_count)}</div>
          </div>
        </div>
        <div className="border-t pt-3 flex items-center justify-between text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Disputes pending review</div>
            <div className="font-semibold text-gray-900" data-testid="fin-disputes-pending">{fmtNum(data.disputes_pending)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Dispute value recovered</div>
            <div className="font-semibold text-emerald-700">{fmtMoney(data.dispute_value_recovered)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Three-hop status pill ─────────────────────────────────────────────────
const HopPill = ({ label, icon: Icon, state, sub }) => {
  const tone = state === 'ok' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : state === 'fail' ? 'text-red-700 bg-red-50 border-red-200'
    : state === 'pending' ? 'text-gray-600 bg-gray-50 border-gray-200'
    : 'text-amber-700 bg-amber-50 border-amber-200';
  return (
    <div className={`flex-1 min-w-0 rounded-lg border ${tone} p-3`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-sm mt-1">{sub || (state === 'ok' ? 'Verified' : state === 'fail' ? 'Failed' : 'Pending')}</div>
    </div>
  );
};

// ── Job log drill-down modal ──────────────────────────────────────────────
const JobLogModal = ({ jobId, onClose, onRefresh }) => {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/delivery-integrity/jobs/${jobId}`, { withCredentials: true });
      setJob(r.data);
    } catch (e) {
      toast.error('Job not found');
      onClose();
    }
  }, [jobId, onClose]);
  useEffect(() => { load(); }, [load]);

  const recover = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/delivery-integrity/jobs/${jobId}/recover`, {}, { withCredentials: true });
      toast.success(r.data.ok ? 'Recovered successfully' : `Recovery failed · ${r.data.attribution || 'unknown'}`);
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error('Recovery request failed');
    } finally {
      setBusy(false);
    }
  };

  if (!job) return null;
  const kieState = job.kie_ai_completed_at ? 'ok' : 'pending';
  const kaharianState = job.kaharian_verify_status === 'pass' ? 'ok'
    : job.kaharian_verify_status === 'fail' ? 'fail'
    : 'pending';
  const konnectState = job.konnect_committed_at ? 'ok'
    : job.konnect_released_at ? 'fail'
    : job.konnect_verified_at ? 'amber' : 'pending';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="job-log-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Job {shortId(job.job_id)} · {job.category} · {job.action_code}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap text-xs">
            <Badge variant="outline">status · {job.status}</Badge>
            {job.attribution && (
              <Badge className={INTERNAL_KEYS.includes(job.attribution) ? 'bg-amber-100 text-amber-900' : 'bg-red-100 text-red-900'}>
                {ATTR_LABEL[job.attribution] || job.attribution}
              </Badge>
            )}
            <Badge variant="outline">actor · {job.actor_email || 'system'}</Badge>
            <Badge variant="outline">created · {fmtDate(job.created_at)}</Badge>
          </div>

          {/* Three-hop pills */}
          <div className="flex gap-2">
            <HopPill label="KIE.ai" icon={Cpu} state={kieState}
              sub={job.kie_ai_job_id ? `job ${shortId(job.kie_ai_job_id)}` : 'no upstream id'} />
            <HopPill label="Kaharian" icon={Cloud} state={kaharianState}
              sub={job.kaharian_verify_status ? `verify · ${job.kaharian_verify_status}` : 'not verified'} />
            <HopPill label="Konnect" icon={Layers} state={konnectState}
              sub={job.konnect_committed_at ? `committed ${fmtDate(job.konnect_committed_at).split(',')[1] || ''}` :
                   job.konnect_released_at ? 'refunded' :
                   job.playback_confirmed_at ? 'playback confirmed' : 'no playback yet'} />
          </div>

          {/* Financial */}
          <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase text-gray-500">KIE.ai charge</div>
              <div className="font-semibold">{fmtMoney(job.kie_ai_charge)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-gray-500">Konnect commit</div>
              <div className="font-semibold">{job.konnect_committed_at ? 'yes' : 'no'}</div>
            </div>
          </div>

          {/* Verification */}
          {job.verification && (
            <div className="border rounded-lg p-3">
              <div className="text-xs font-semibold uppercase text-gray-700 mb-2">Verification</div>
              <pre className="text-xs bg-gray-50 rounded p-2 overflow-x-auto">
                {JSON.stringify(job.verification, null, 2)}
              </pre>
            </div>
          )}

          {/* Kaharian receipt */}
          {job.kaharian_receipt && (
            <div className="border rounded-lg p-3">
              <div className="text-xs font-semibold uppercase text-gray-700 mb-2">Kaharian delivery receipt</div>
              <pre className="text-xs bg-gray-50 rounded p-2 overflow-x-auto max-h-40">
                {JSON.stringify(job.kaharian_receipt, null, 2)}
              </pre>
            </div>
          )}

          {/* Recovery ladder trace */}
          <div className="border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase text-gray-700">Recovery ladder</div>
              <Button size="sm" onClick={recover} disabled={busy} data-testid="run-recovery-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${busy ? 'animate-spin' : ''}`} />
                Run recovery ladder
              </Button>
            </div>
            {(job.recovery_ladder || []).length === 0 && (
              <div className="text-xs text-gray-500 italic">No recovery attempts yet.</div>
            )}
            <div className="space-y-1">
              {(job.recovery_ladder || []).map((step, i) => (
                <div key={i} className="text-xs flex items-center gap-2 border-l-2 pl-2"
                  style={{ borderColor: step.outcome === 'recovered' ? '#10b981' : step.outcome === 'failed' ? '#ef4444' : '#9ca3af' }}>
                  {step.outcome === 'recovered' ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> :
                   step.outcome === 'failed' ? <XCircle className="w-3 h-3 text-red-600" /> :
                   <Clock className="w-3 h-3 text-gray-500" />}
                  <span className="font-mono">{step.step}</span>
                  <span className="text-gray-500">· {step.outcome}</span>
                  {step.notes && <span className="text-gray-400 truncate">· {step.notes}</span>}
                  <span className="text-gray-400 ml-auto">{fmtDate(step.at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────
export default function DeliveryIntegrityPage() {
  const [tiles, setTiles] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attrFilter, setAttrFilter] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tRes, jRes, dRes] = await Promise.all([
        axios.get(`${API}/api/delivery-integrity/tiles`, { withCredentials: true }),
        axios.get(`${API}/api/delivery-integrity/jobs?limit=100${attrFilter ? `&attribution=${attrFilter}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/api/delivery-integrity/disputes`, { withCredentials: true }),
      ]);
      setTiles(tRes.data);
      setJobs(jRes.data.jobs || []);
      setDisputes(dRes.data.disputes || []);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Operator access required');
      else toast.error('Failed to load delivery integrity data');
    } finally {
      setLoading(false);
    }
  }, [attrFilter]);

  useEffect(() => { load(); }, [load]);

  const runReconciliation = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/delivery-integrity/reconciliation/run`, {}, { withCredentials: true });
      toast.success(`Reconciliation complete · $${r.data.total_kie_ai_charges.toFixed(4)} in window`);
      await load();
    } catch { toast.error('Reconciliation failed'); }
    setBusy(false);
  };

  const runRetroSweep = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/delivery-integrity/retro-sweep?days=90&limit=5000`, {}, { withCredentials: true });
      toast.success(`Retro sweep · scanned ${r.data.scanned} · recovered ${r.data.recovered} · refunded ${r.data.refunded_konnect} · backfilled ${r.data.backfilled}`);
      await load();
    } catch { toast.error('Retro sweep failed'); }
    setBusy(false);
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-testid="delivery-integrity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-emerald-600" />
            Delivery Integrity & Reconciliation
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Three-tier chain · KIE.ai → Kaharian → Konnect · trusted-until-proven-otherwise
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={runReconciliation} disabled={busy} data-testid="run-reconciliation-btn">
            Run reconciliation
          </Button>
          <Button size="sm" onClick={runRetroSweep} disabled={busy} data-testid="run-retro-sweep-btn">
            <PlayCircle className="w-4 h-4 mr-1" /> Run 90-day retro sweep
          </Button>
        </div>
      </div>

      {/* Two operator tiles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DeliveryIntegrityTile data={tiles?.delivery_integrity} />
        <FinancialReconciliationTile data={tiles?.financial_reconciliation} />
      </div>

      {/* Tabs: Jobs · Disputes */}
      <Tabs defaultValue="jobs" className="w-full">
        <TabsList data-testid="di-tabs">
          <TabsTrigger value="jobs" data-testid="tab-jobs">Job ledger ({jobs.length})</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="tab-disputes">
            Disputes ({disputes.length})
            {disputes.length > 0 && <AlertTriangle className="w-3 h-3 ml-1 text-amber-600" />}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4" /> Recent generation jobs
                </CardTitle>
                <div className="flex gap-1 flex-wrap">
                  <Button size="sm" variant={attrFilter === '' ? 'default' : 'outline'}
                    onClick={() => setAttrFilter('')} data-testid="filter-all">All</Button>
                  {INTERNAL_KEYS.map(k => (
                    <Button key={k} size="sm"
                      variant={attrFilter === k ? 'default' : 'outline'}
                      onClick={() => setAttrFilter(k)}
                      data-testid={`filter-${k}`}
                    >
                      {ATTR_LABEL[k].split('·')[1]?.trim()}
                    </Button>
                  ))}
                  <Button size="sm" variant={attrFilter === 'upstream_kie_ai' ? 'default' : 'outline'}
                    onClick={() => setAttrFilter('upstream_kie_ai')} data-testid="filter-upstream">
                    Upstream
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No jobs match this filter. That's a clean chain.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-gray-500 border-b">
                      <tr>
                        <th className="text-left py-2 px-2">Job</th>
                        <th className="text-left py-2 px-2">Category</th>
                        <th className="text-left py-2 px-2">Action</th>
                        <th className="text-right py-2 px-2">Charge</th>
                        <th className="text-left py-2 px-2">Status</th>
                        <th className="text-left py-2 px-2">Attribution</th>
                        <th className="text-left py-2 px-2">Created</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map(j => (
                        <tr
                          key={j.job_id}
                          data-testid={`job-row-${j.job_id}`}
                          className="border-b hover:bg-gray-50 cursor-pointer"
                          onClick={() => setSelectedJob(j.job_id)}
                        >
                          <td className="py-2 px-2 font-mono text-xs">{shortId(j.job_id)}</td>
                          <td className="py-2 px-2">{j.category}</td>
                          <td className="py-2 px-2 text-xs">{j.action_code}</td>
                          <td className="py-2 px-2 text-right font-mono text-xs">{fmtMoney(j.kie_ai_charge)}</td>
                          <td className="py-2 px-2">
                            <Badge variant="outline" className="text-[10px]">{j.status}</Badge>
                          </td>
                          <td className="py-2 px-2 text-xs">
                            {j.attribution ? (
                              <Badge className={INTERNAL_KEYS.includes(j.attribution)
                                ? 'bg-amber-100 text-amber-900'
                                : j.attribution === 'upstream_kie_ai' ? 'bg-red-100 text-red-900'
                                : 'bg-gray-100 text-gray-800'}>
                                {ATTR_LABEL[j.attribution] || j.attribution}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-xs text-gray-500">{fmtDate(j.created_at).split(',')[0]}</td>
                          <td className="py-2 px-2"><ChevronRight className="w-4 h-4 text-gray-400" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="disputes" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Prepared dispute payloads · never auto-filed
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Every payload has been prepared with full evidence (KIE.ai job id, recovery attempts,
                verification failures, timestamps). Review before filing manually to preserve the
                vendor relationship.
              </p>
            </CardHeader>
            <CardContent>
              {disputes.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500 opacity-40" />
                  No disputes pending. Given KIE.ai's reliability, this should stay near zero.
                </div>
              ) : (
                <div className="space-y-2">
                  {disputes.map(d => (
                    <div key={d.job_id}
                      data-testid={`dispute-row-${d.job_id}`}
                      className="border rounded-lg p-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {d.category} · {fmtMoney(d.kie_ai_charge)}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          job {shortId(d.job_id)} · kie {shortId(d.kie_ai_job_id)}
                        </div>
                        <div className="text-xs text-gray-500">
                          prepared {fmtDate(d.prepared_at)} · {(d.recovery_ladder || []).length} recovery steps tried
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setSelectedJob(d.job_id)}>
                        Review
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {selectedJob && (
        <JobLogModal
          jobId={selectedJob}
          onClose={() => setSelectedJob(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}
